import React, {useState} from "react";
import {
    DSGVOCheckbox,
    Form,
    InputField,
    RadioGroup,
    SecondaryButton,
    SubmitButton,
    TextAreaField
} from "../Form/Form";
import {__} from "@wordpress/i18n";
import {submitForm} from "../api/requests";
import {CheckboxGroup} from "./components/CheckboxGroup";

const hasMinLength = (value: string, minLength: number): boolean =>
    value.trim().length >= minLength;

const isEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isOneOf = (allowedValues: string[]) => (value: string): boolean =>
    allowedValues.includes(value.trim());

const serviceOptions = [
    {label: 'Cloud Infrastructure', value: 'Cloud Infrastructure'},
    {label: 'Cyber Security', value: 'Cyber Security'},
    {label: 'Managed Services', value: 'Managed Services'},
    {label: 'Modern Workplace', value: 'Modern Workplace'},
    {label: 'Network & Connectivity', value: 'Network & Connectivity'},
    {label: 'Data Solutions', value: 'Data Solutions'},
];

const companySizeOptions = [
    {label: '1-10', value: '1-10'},
    {label: '11-50', value: '11-50'},
    {label: '51-200', value: '51-200'},
    {label: '200+', value: '200+'},
];

export function MultiStepContactForm(): React.ReactElement {
    const [currentStep, updateCurrentStep] = useState(0);
    const [selectedService, setSelectedService] = useState(serviceOptions[0].value);
    const [selectedCompanySize, setSelectedCompanySize] = useState(companySizeOptions[0].value);

    const onSubmit = async (event: SubmitEvent): Promise<void> => {
        event.preventDefault();

        const form = event.target as HTMLFormElement | null;

        if (!form) {
            return;
        }

        const formData = new FormData(form);

        await submitForm('multistep-contact-form', {
            service: selectedService,
            company_size: selectedCompanySize,
            company: (formData.get('company') ?? '').toString(),
            function: (formData.get('function') ?? '').toString(),
            name: (formData.get('name') ?? '').toString(),
            email: (formData.get('email') ?? '').toString(),
            message: (formData.get('message') ?? '').toString(),
            dsgvo: formData.has('dsgvo'),
        });
    };

    const onSubmitFirstStep = async (event: SubmitEvent): Promise<void> => {
        event.preventDefault();

        const form = event.target as HTMLFormElement | null;

        if (!form) {
            return;
        }

        const formData = new FormData(form);
        const service = (formData.get('service') ?? '').toString();

        setSelectedService(service);
        updateCurrentStep(1);
    };

    const onSubmitSecondStep = async (event: SubmitEvent): Promise<void> => {
        event.preventDefault();

        const form = event.target as HTMLFormElement | null;

        if (!form) {
            return;
        }

        const formData = new FormData(form);
        const companySize = (formData.get('company_size') ?? '').toString();

        setSelectedCompanySize(companySize);
        updateCurrentStep(2);
    };

    const stepClassName = (stepIndex: number): string => {
        return [
            "wvcb-step-item",
            currentStep === stepIndex ? "is-active" : null,
            currentStep > stepIndex ? "is-completed" : null,
        ].filter(Boolean).join(" ");
    };

    return (
        <>
            <p>{__('Vielen Dank für die Auswahl. Du hast dich für die ausführliche Anfrage entschieden. ', 'wv-chat-bot')}</p>
            <div className="wvcb-card">
                <ol className={`wvcb-multi-step-indicator wvcb-multi-step-indicator--step-${currentStep}`}>
                    <li className={stepClassName(0)} data-step="1">
                        <span className="wvcb-step-item__label">Leistung bestimmen</span>
                    </li>
                    <li className={stepClassName(1)} data-step="2">
                        <span className="wvcb-step-item__label">Unternehmensgröße bestimmen</span>
                    </li>
                    <li className={stepClassName(2)} data-step="3">
                        <span className="wvcb-step-item__label">Kontaktdaten angeben</span>
                    </li>
                </ol>
            </div>
            <div className="wvcb-card">
                <div className="wvcb-contact-form wvcb-contact-form--steps">
                    {currentStep === 0 ? (
                        <Form onSubmit={onSubmitFirstStep}>
                            <div className="container grid wrap">
                                <div className="column">
                                    <CheckboxGroup
                                        label={__('Leistung auswählen', 'wv-chat-bot')}
                                        name="service"
                                        required
                                        options={serviceOptions}
                                        defaultValue={selectedService}
                                        validate={isOneOf(serviceOptions.map((option) => option.value))}
                                        validationMessage={__('Bitte eine gueltige Leistung auswaehlen.', 'wv-chat-bot')}
                                    />
                                </div>
                                <div className="column">
                                    <div className="buttons">
                                        <SubmitButton label={__('Weiter', 'wv-chat-bot')}/>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    ) : null}

                    {currentStep === 1 ? (
                        <Form onSubmit={onSubmitSecondStep}>
                            <div className="container grid wrap">
                                <div className="column">
                                    <RadioGroup
                                        label={__('Unternehmensgröße', 'wv-chat-bot')}
                                        name="company_size"
                                        required
                                        options={companySizeOptions}
                                        defaultValue={selectedCompanySize}
                                        validate={isOneOf(companySizeOptions.map((option) => option.value))}
                                        validationMessage={__('Bitte eine gueltige Unternehmensgröße auswaehlen.', 'wv-chat-bot')}
                                    />
                                </div>
                                <div className="column">
                                    <div className="buttons">
                                        <SubmitButton label={__('Weiter', 'wv-chat-bot')}/>
                                        <SecondaryButton
                                            label={__('Zurück', 'wv-chat-bot')}
                                            onClick={() => updateCurrentStep(0)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Form>
                    ) : null}

                    {currentStep === 2 ? (
                        <Form onSubmit={onSubmit}>
                            <div className="container grid wrap">
                                <div className="column column--md-6">
                                    <InputField
                                        label={__('Unternehmen', 'wv-chat-bot')}
                                        name="company"
                                        required
                                        validate={(value) => hasMinLength(value, 2)}
                                        validationMessage={__('Bitte einen gueltigen Unternehmensnamen eingeben.', 'wv-chat-bot')}
                                    />
                                </div>
                                <div className="column column--md-6">
                                    <InputField
                                        label={__('Funktion', 'wv-chat-bot')}
                                        name="function"
                                        required
                                        validate={(value) => hasMinLength(value, 2)}
                                        validationMessage={__('Bitte eine Funktion angeben.', 'wv-chat-bot')}
                                    />
                                </div>
                                <div className="column column--md-6">
                                    <InputField
                                        label={__('Vor- und Nachname', 'wv-chat-bot')}
                                        name="name"
                                        required
                                        validate={(value) => value.trim().split(/\s+/).length >= 2}
                                        validationMessage={__('Bitte Vor- und Nachname eingeben.', 'wv-chat-bot')}
                                    />
                                </div>
                                <div className="column column--md-6">
                                    <InputField
                                        label={__('E-Mail', 'wv-chat-bot')}
                                        name="email"
                                        type="email"
                                        required
                                        validate={isEmail}
                                        validationMessage={__('Bitte eine gueltige E-Mail-Adresse eingeben.', 'wv-chat-bot')}
                                    />
                                </div>
                                <div className="column">
                                    <TextAreaField
                                        label={__('Freitext', 'wv-chat-bot')}
                                        name="message"
                                        required
                                        validate={(value) => hasMinLength(value, 10)}
                                        validationMessage={__('Bitte mindestens 10 Zeichen im Freitext eingeben.', 'wv-chat-bot')}
                                    />
                                </div>
                                <div className="column">
                                    <DSGVOCheckbox/>
                                </div>
                                <div className="column">
                                    <div className="buttons">
                                        <SubmitButton label={__('Absenden', 'wv-chat-bot')}/>
                                        <SecondaryButton
                                            label={__('Zurück', 'wv-chat-bot')}
                                            onClick={() => updateCurrentStep(1)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Form>
                    ) : null}
                </div>
            </div>
        </>
    );
}
